//@flow

// Vendors
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import classNames from 'classnames';

// Components
import { FormActions, FormActionsItem, LoadingDots, Trans } from '../../';
import { Modal } from '../../modals';
import { PaymentCardFormWrapper } from '../../../';
import { TxButton, TxButtonMode } from 'texkit-ui';
import { TxPaymentCardForm, TxPaymentCardFormMode } from 'texkit-ui/forms';

// Actions
import {
  editPaymentAccount,
  getPaymentAccount,
  getPaymentAccounts,
} from '../../../../reducers/payment/paymentActions';
import {
  openHoustonErrorModal,
  openModal,
  closeModal,
} from '../../../../reducers/modal/modalActions';
import {
  AUTHOR_TYPES,
  pushMessageBanner,
} from '../../../../reducers/messageBanner/messageBannerActions';

// Selectors
import { selectModalData } from '../../../../reducers/modal/modalSelectors';
import { selectAppLanguage } from '../../../../reducers/app/appSelectors';

// Helpers
import { states } from 'texkit-ui/utils';
import {
  formatExpirationDate,
  getCurrentFormattedTime,
} from '../../../../lib/helpers';
import { selectAppSlugByPath } from '../../../../reducers/agency/agencySelectors';

import './edit-payment-account-modal.scss';

// Switch to adjusted SCSS if on iOS device
const userAgent = window.navigator.userAgent.toLowerCase();
const ios = /iphone|ipod|ipad/.test(userAgent);

const EditPaymentAccountModal = () => {
  const formikPropsRef = useRef();
  const dispatch = useDispatch();
  const [initialValues, setInitialValues] = useState();
  const { paymentAccount } = useSelector(selectModalData);
  const selectedLanguage = useSelector(selectAppLanguage);
  const currentAppSlug = useSelector(selectAppSlugByPath);
  const isAccountSettings = currentAppSlug === 'accountSettings';
  const [isFormValid, setIsFormValid] = useState(true);
  const [isFormFilled, setIsFormFilled] = useState(false);

  const fetchPaymentAccount = async paymentAccount => {
    try {
      const { paymentAccountId } = paymentAccount;
      const { status, data } = await dispatch(
        getPaymentAccount(paymentAccountId)
      );

      if (status === 'ERROR') {
        dispatch(openModal('ServerErrorModal'));
        setInitialValues();
        return;
      }

      const { cardLast4, expirationDate } = data;
      const address = data.paymentRequest.address || {};
      const state = states.find(state => state.value === address.stateCd);

      const initialAccountInfo = {
        cardNumber: cardLast4,
        expirationDate: formatExpirationDate(expirationDate),
        name: data.paymentRequest.paymentAccountName || '',
        streetAddress: address.addressLine1,
        streetAddress2: address.addressLine2,
        city: address.city,
        state: state ? state.value : '',
        zip: address.zipCode,
      };
      setInitialValues(initialAccountInfo);
      setTimeout(() => {
        if (formikPropsRef.current) {
          formikPropsRef.current.validateForm();
        }
      }, 0);
    } catch (error) {
      console.error('Payment account error');
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const { paymentAccountId } = paymentAccount;
    const expirationDate = values.expirationDate.replace('/', '');
    const addressLine2 = values.streetAddress2 || '';

    const payload = {
      paymentAccountId,
      expirationDate,
      paymentAccountName: values.name,
      address: {
        streetAddress: values.streetAddress,
        addressLine2,
        city: values.city,
        state: values.state,
        postalCode: values.zip,
      },
    };

    try {
      await dispatch(editPaymentAccount(payload));

      if (isAccountSettings) {
        dispatch(
          pushMessageBanner([
            {
              messageId: Date.now().toString(),
              textEnglish: '{"type": "Alerts", "id": "SuccessCardUpdated"}',
              textSpanish: '{"type": "Alerts", "id": "SuccessCardUpdated"}',
              startTime: getCurrentFormattedTime(),
              endTime: getCurrentFormattedTime(60),
              priority: '4',
              status: 'R',
              author: AUTHOR_TYPES.ACTION_RESULT,
              valid: true,
              dismissable: true,
              expiresIn: 60000,
            },
          ])
        );
      }

      dismissModal();
    } catch (error) {
      const options = {
        navigateOnClose: false,
      };
      dispatch(openHoustonErrorModal(options));
    } finally {
      setSubmitting(false);
    }

    return;
  };

  const dismissModal = () => {
    formikPropsRef.current = null;
    setInitialValues();
    dispatch(getPaymentAccounts());
    dispatch(closeModal('EditPaymentAccountModal'));
  };

  return (
    <Modal
      name="EditPaymentAccountModal"
      className={
        ios ? 'edit-payment-account-modal ios' : 'edit-payment-account-modal'
      }
      disableOnClickOutside="true"
      closeButtonClassName="modal-close-button"
      onClose={dismissModal}
      onOpen={() => fetchPaymentAccount(paymentAccount)}
    >
      <h2 className="h1 modal-title">
        <Trans
          file="Payment"
          id="UpdateCardInformation"
          fallback="Update card information"
        />
      </h2>
      {!initialValues ? (
        <LoadingDots />
      ) : (
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {formikProps => {
            if (!formikPropsRef.current) {
              formikPropsRef.current = formikProps;
            }
            const { isSubmitting } = formikProps;

            const isSubmitDisabled =
              !isFormValid || isSubmitting || !isFormFilled;

            return (
              <form noValidate>
                <PaymentCardFormWrapper formikProps={formikProps}>
                  <TxPaymentCardForm
                    displayName="TxPaymentCardForm"
                    paymentMethodTitle={
                      <Trans file="Payment" id="CardInformationTitle" />
                    }
                    paymentMethodSubTitle={
                      !isAccountSettings && (
                        <Trans file="Payment" id="PaymentMethodSubTitle" />
                      )
                    }
                    billingAddressTitle={
                      <Trans file="Payment" id="BillingAddressTitle" />
                    }
                    billingAddressSubTitle={
                      !isAccountSettings && (
                        <Trans file="Payment" id="BillingAddressSubTitle" />
                      )
                    }
                    mode={TxPaymentCardFormMode.Edit}
                    spanish={selectedLanguage === 'es'}
                    onFormError={errors => {
                      const cleanedErrors = JSON.parse(JSON.stringify(errors));
                      const hasErrors = Object.keys(cleanedErrors).length > 0;
                      setIsFormValid(!hasErrors);
                    }}
                    onFormFilled={isFilled => {
                      setIsFormFilled(isFilled);
                    }}
                  />

                  <FormActions>
                    <FormActionsItem
                      className={classNames({
                        'spanish-only': selectedLanguage === 'es',
                      })}
                    >
                      <TxButton
                        mode={TxButtonMode.Primary}
                        disabled={isSubmitDisabled}
                        onClick={() => formikProps.handleSubmit()}
                      >
                        <Trans file="Payment" id="SavePaymentMethod" />
                      </TxButton>
                    </FormActionsItem>
                  </FormActions>
                </PaymentCardFormWrapper>
              </form>
            );
          }}
        </Formik>
      )}
    </Modal>
  );
};

export default EditPaymentAccountModal;
